/**
 * Pure helpers for artifact rendering — parsing, filenames, size labels.
 * Kept free of React so they unit-test without a DOM.
 */

import type { ArtifactData } from '../../lib/artifacts/types.js';

/** Extension for an image artifact, derived from the stored mime type. */
const IMAGE_EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/avif': 'avif',
  'image/gif': 'gif',
};

export interface ParsedArtifactTable {
  columns: string[];
  rows: Array<Record<string, unknown>>;
}

/**
 * Parse `table` artifact content: a JSON array of (near-)uniform objects.
 * Returns null on ANY shape mismatch — the viewer then falls back to the code
 * view instead of crashing (tested invariant).
 */
export function parseArtifactTable(content: string): ParsedArtifactTable | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    return null;
  }

  if (!Array.isArray(parsed) || parsed.length === 0) return null;

  const rows: Array<Record<string, unknown>> = [];
  const columns: string[] = [];
  const seen = new Set<string>();

  for (const row of parsed) {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) return null;
    const record = row as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    }
    rows.push(record);
  }

  if (columns.length === 0) return null;
  return { columns, rows };
}

/** Render one table cell value as quiet text. */
export function formatTableCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const serialized = JSON.stringify(value);
  return serialized === undefined ? String(value) : serialized;
}

/**
 * Size label for the card/viewer chrome.
 *
 * For an image artifact the `content` is a URL, so its length says nothing
 * about the picture — the real byte size rides in `artifact.image`. Falling back
 * to the content length there would print "84 chars" next to a 400KB photo.
 */
export function artifactSizeLabel(
  artifact: Pick<ArtifactData, 'kind' | 'content' | 'image'>
): string {
  if (artifact.kind === 'image') {
    const bytes = artifact.image?.byteLength;
    return typeof bytes === 'number' ? formatArtifactBytes(bytes) : 'image';
  }
  return formatArtifactSize(artifact.content.length);
}

/** Human byte-size label (real bytes, not chars). */
export function formatArtifactBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb >= 100 ? Math.round(kb) : kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** Human size label from a char count (chars ≈ bytes for our purposes). */
export function formatArtifactSize(chars: number): string {
  if (chars < 1024) return `${chars} chars`;
  const kb = chars / 1024;
  if (kb < 1024) return `${kb >= 100 ? Math.round(kb) : kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

const CODE_EXTENSIONS: Record<string, string> = {
  typescript: 'ts',
  tsx: 'tsx',
  javascript: 'js',
  jsx: 'jsx',
  python: 'py',
  ruby: 'rb',
  rust: 'rs',
  kotlin: 'kt',
  csharp: 'cs',
  'c#': 'cs',
  cpp: 'cpp',
  'c++': 'cpp',
  c: 'c',
  java: 'java',
  go: 'go',
  php: 'php',
  swift: 'swift',
  sql: 'sql',
  bash: 'sh',
  shell: 'sh',
  sh: 'sh',
  powershell: 'ps1',
  yaml: 'yaml',
  yml: 'yaml',
  json: 'json',
  html: 'html',
  css: 'css',
  scss: 'scss',
  markdown: 'md',
  xml: 'xml',
};

function slugifyTitle(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug || 'artifact';
}

/** Extension for downloads, per kind (and language for code). */
export function artifactFileExtension(
  artifact: Pick<ArtifactData, 'kind' | 'language' | 'image'>
): string {
  switch (artifact.kind) {
    case 'markdown':
      return 'md';
    case 'html':
      return 'html';
    case 'table':
      return 'json';
    // A chart artifact IS its spec — downloading the JSON gives back something
    // that can be re-rendered, which a rasterized image would not.
    case 'chart':
      return 'json';
    case 'code': {
      const key = artifact.language?.trim().toLowerCase() ?? '';
      return CODE_EXTENSIONS[key] ?? 'txt';
    }
    case 'image':
      return IMAGE_EXTENSIONS[artifact.image?.mimeType ?? ''] ?? 'png';
  }
}

/** Download filename: slugged title + kind-appropriate extension. */
export function artifactFileName(
  artifact: Pick<ArtifactData, 'title' | 'kind' | 'language' | 'image'>
): string {
  return `${slugifyTitle(artifact.title)}.${artifactFileExtension(artifact)}`;
}

/**
 * Widest table that earns an inline preview.
 *
 * There is no ROW cap: the inline table shows its first few rows and offers
 * "show all N rows", so a 500-row result is still a useful glance. Columns are
 * different — past ~8 the inline card is a horizontal-scroll maze, and the
 * expanded view (with its filter and sortable headers) is the honest place for
 * it.
 */
export const INLINE_TABLE_MAX_COLUMNS = 8;

/** Which artifacts render their content directly in the message flow. */
export type ArtifactInlinePreview = 'image' | 'chart' | 'table';

/**
 * Should this artifact show its content in the message flow, or stay collapsed
 * behind "Open"?
 *
 * The test is not "is it visual" — it is **is the content itself the answer, and
 * is it small enough to take in at a glance**. A cropped photo, a bar chart, and
 * a five-row comparison are all answers you either see or you don't; making
 * someone click to find out whether the crop is right, or what the chart says,
 * is the whole complaint. A report, a source file, an HTML page and a 200-row
 * table are all things you *open* — inlining them would bury the conversation,
 * and for html specifically it would also mean running a sandboxed iframe
 * unprompted in the middle of the transcript.
 *
 * Pure and separate from the components so the policy is unit-testable without
 * a DOM.
 */
export function artifactInlinePreview(
  artifact: Pick<ArtifactData, 'kind' | 'content'>
): ArtifactInlinePreview | null {
  switch (artifact.kind) {
    case 'image':
      return 'image';
    // A chart is static SVG with no scripts and no network — rendering it in
    // the flow costs nothing beyond the markup already in the part.
    case 'chart':
      return 'chart';
    case 'table': {
      const parsed = parseArtifactTable(artifact.content);
      if (!parsed) return null;
      return parsed.columns.length <= INLINE_TABLE_MAX_COLUMNS ? 'table' : null;
    }
    case 'markdown':
    case 'code':
    case 'html':
      return null;
  }
}

/** Short human label for the kind chip. */
export function artifactKindLabel(artifact: Pick<ArtifactData, 'kind' | 'language'>): string {
  if (artifact.kind === 'code' && artifact.language) return artifact.language;
  switch (artifact.kind) {
    case 'markdown':
      return 'Document';
    case 'code':
      return 'Code';
    case 'html':
      return 'HTML';
    case 'chart':
      return 'Chart';
    case 'table':
      return 'Table';
    case 'image':
      return 'Image';
  }
}
